import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Badge,
  Divider,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import UserSearchPicker from "./UserSearchPicker";
import SchemaBadge from "./SchemaBadge";
import { AGENT_SENDER_ID } from "../../pages/medstudent/agents/agentsService";

const PRESENCE_DOT = {
  online: { color: "green.400", label: "Online" },
  idle: { color: "yellow.400", label: "Idle" },
  offline: { color: "gray.400", label: "Offline" },
};

function PresenceDot({ status }) {
  const dot = PRESENCE_DOT[status] || PRESENCE_DOT.offline;
  return (
    <Box
      as="span"
      role="img"
      aria-label={dot.label}
      title={dot.label}
      data-presence={status || "offline"}
      flexShrink={0}
      w="10px"
      h="10px"
      borderRadius="full"
      bg={dot.color}
    />
  );
}

// Slack's unread display (docs/specs/messaging-notifications-spec.md): a channel with unread
// messages is bold; the number is only its mentions (a DM's every message is one). A muted
// channel is dimmed, never bold, and has a number only when someone mentioned the reader there.
// mentionCount undefined (a caller without the mention counts): the number is the unread count,
// as before mentions existed.
function ChannelRow({ channel, isActive, unreadCount, mentionCount, muted = false, onSelect, onDelete, canDelete, presenceStatus }) {
  const badge = mentionCount === undefined ? (muted ? 0 : unreadCount) : mentionCount;
  const bold = isActive || (unreadCount > 0 && !muted);
  // An agent's thread is called after its agent ("<robot> <title>"), without the channel's "#".
  const label =
    channel.channel_type === "dm"
      ? channel.dm_label || "Direct message"
      : channel.agent_id
        ? channel.name
        : `# ${channel.name}`;
  // NotesAI Agent is always there and has no presence. Told by id, never by name: a person can be
  // called "NotesAI Agent" too, and keeps their dot.
  const isAgentDm = channel.channel_type === "dm" && channel.other_user_id === AGENT_SENDER_ID;
  return (
    <HStack
      p={2}
      borderRadius="md"
      cursor="pointer"
      bg={isActive ? "#2F5BEA" : "transparent"}
      color={isActive ? "white" : "#0A1F44"}
      _hover={{ bg: isActive ? "#2F5BEA" : "blackAlpha.100" }}
      onClick={() => onSelect(channel)}
      justify="space-between"
      opacity={muted && !isActive ? 0.55 : 1}
      data-unread={unreadCount > 0 && !muted ? "true" : undefined}
      data-muted={muted ? "true" : undefined}
    >
      <HStack spacing={2} minW={0}>
        {channel.channel_type === "dm" &&
          (isAgentDm ? (
            // Where the dot would be, so the agent's name lines up with the other DMs'.
            <Box as="span" aria-hidden="true" flexShrink={0} w="10px" />
          ) : (
            <PresenceDot status={presenceStatus} />
          ))}
        <Text fontWeight={bold ? "bold" : "normal"} noOfLines={1}>
          {label}
        </Text>
        {channel.channel_type === "dm" && <SchemaBadge schema={channel.dm_schema} />}
      </HStack>
      <HStack spacing={1}>
        {badge > 0 && (
          <Badge bg="#14B8A6" color="white" borderRadius="full">
            {badge}
          </Badge>
        )}
        {canDelete && (
          <IconButton
            aria-label="Delete channel"
            icon={<DeleteIcon />}
            size="xs"
            variant="ghost"
            color="inherit"
            _hover={{ bg: "blackAlpha.200" }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(channel);
            }}
          />
        )}
      </HStack>
    </HStack>
  );
}

// unreadCounts / mentionCounts / channelSettings: the unread summary's counts, mentions and
// settings (utils/messagingNotifications.js).
export default function ChannelList({
  channels,
  activeChannelId,
  unreadCounts,
  mentionCounts,
  channelSettings,
  presence,
  canManageChannels,
  schema,
  onSelect,
  onJoin,
  onDelete,
  onRequestCreateChannel,
  onStartDm,
}) {
  const [showDmPicker, setShowDmPicker] = useState(false);
  const mine = channels?.mine || [];
  const joinable = channels?.joinable || [];
  // Every agent has a thread of its own (Plan 4 Task 9), told by its agent_id, never by the robot in
  // its name: they are listed under Agents, and nowhere else.
  const agentThreads = mine.filter((c) => c.agent_id);
  const others = mine.filter((c) => !c.agent_id);
  const publicChannels = others.filter((c) => c.channel_type === "public");
  const privateChannels = others.filter((c) => c.channel_type === "private");
  const dms = others.filter((c) => c.channel_type === "dm");

  return (
    <Box w="240px" bg="#E6F2F8" color="#0A1F44" borderRight="1px solid" borderColor="#C5DDEA" h="100%" overflowY="auto" p={3}>
      <HStack justify="space-between" mb={2}>
        <Text fontWeight="bold" fontSize="xs" letterSpacing="wide" color="#2F5BEA">
          CHANNELS
        </Text>
        {canManageChannels && (
          <IconButton
            aria-label="Create channel"
            icon={<AddIcon />}
            size="xs"
            variant="ghost"
            color="#0A1F44"
            _hover={{ bg: "blackAlpha.200" }}
            onClick={onRequestCreateChannel}
          />
        )}
      </HStack>
      <VStack align="stretch" spacing={1} mb={3}>
        {publicChannels.concat(privateChannels).map((c) => (
          <ChannelRow
            key={c.id}
            channel={c}
            isActive={c.id === activeChannelId}
            unreadCount={unreadCounts?.[c.id] || 0}
            mentionCount={mentionCounts ? mentionCounts[c.id] || 0 : undefined}
            muted={!!channelSettings?.[c.id]?.muted}
            onSelect={onSelect}
            onDelete={onDelete}
            canDelete={canManageChannels}
          />
        ))}
      </VStack>

      {joinable.length > 0 && (
        <>
          <Text fontWeight="bold" fontSize="xs" letterSpacing="wide" color="#2F5BEA" mb={2}>
            BROWSE CHANNELS
          </Text>
          <VStack align="stretch" spacing={1} mb={3}>
            {joinable.map((c) => (
              <HStack key={c.id} justify="space-between" p={2}>
                <Text noOfLines={1}># {c.name}</Text>
                <Button size="xs" colorScheme="teal" onClick={() => onJoin(c)}>
                  Join
                </Button>
              </HStack>
            ))}
          </VStack>
        </>
      )}

      {agentThreads.length > 0 && (
        <>
          <Text fontWeight="bold" fontSize="xs" letterSpacing="wide" color="#2F5BEA" mb={2}>
            AGENTS
          </Text>
          <VStack align="stretch" spacing={1} mb={3}>
            {agentThreads.map((c) => (
              <ChannelRow
                key={c.id}
                channel={c}
                isActive={c.id === activeChannelId}
                unreadCount={unreadCounts?.[c.id] || 0}
            mentionCount={mentionCounts ? mentionCounts[c.id] || 0 : undefined}
            muted={!!channelSettings?.[c.id]?.muted}
                onSelect={onSelect}
                onDelete={onDelete}
                // Agents are made and deleted in the Agents tab. The backend refuses to delete an
                // agent's thread (400): its members can once the agent is gone.
                canDelete={false}
              />
            ))}
          </VStack>
        </>
      )}

      <Divider mb={3} borderColor="#C5DDEA" />

      <HStack justify="space-between" mb={2}>
        <Text fontWeight="bold" fontSize="xs" letterSpacing="wide" color="#2F5BEA">
          DIRECT MESSAGES
        </Text>
        <IconButton
          aria-label="New direct message"
          icon={<AddIcon />}
          size="xs"
          variant="ghost"
          color="#0A1F44"
          _hover={{ bg: "blackAlpha.200" }}
          onClick={() => setShowDmPicker((v) => !v)}
        />
      </HStack>
      {showDmPicker && (
        <UserSearchPicker
          schema={schema}
          autoFocus
          onCancel={() => setShowDmPicker(false)}
          onPick={(user) => {
            setShowDmPicker(false);
            onStartDm(user);
          }}
        />
      )}
      <VStack align="stretch" spacing={1}>
        {dms.map((c) => (
          <ChannelRow
            key={c.id}
            channel={c}
            isActive={c.id === activeChannelId}
            unreadCount={unreadCounts?.[c.id] || 0}
            mentionCount={mentionCounts ? mentionCounts[c.id] || 0 : undefined}
            muted={!!channelSettings?.[c.id]?.muted}
            onSelect={onSelect}
            onDelete={onDelete}
            canDelete={false}
            presenceStatus={presence?.[c.other_user_id]}
          />
        ))}
        {dms.length === 0 && (
          <Text fontSize="sm" color="#2F5BEA">
            No direct messages yet
          </Text>
        )}
      </VStack>
    </Box>
  );
}
